import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Search, Video, MessageSquare, ExternalLink, Clock, CheckCircle, AlertCircle, Play, X, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CritiqueVideoPlayer = ({ url, label, onClose }) => {
  if (!url) return null;
  const isEmbed = url.includes('vimeo.com') || url.includes('youtube.com') || url.includes('youtu.be');
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-4xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white hover:text-gray-300">
          <X className="w-6 h-6" />
        </button>
        <p className="text-white text-sm mb-2">{label}</p>
        {isEmbed ? (
          <iframe src={url} className="w-full aspect-video rounded-lg" allowFullScreen />
        ) : (
          <video src={url} controls autoPlay className="w-full rounded-lg max-h-[75vh]" />
        )}
      </div>
    </div>
  );
};

const AdminCritiquesTab = () => {
  const [critiques, setCritiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [videoPlayer, setVideoPlayer] = useState(null);

  const fetchCritiques = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/critiques`);
      setCritiques(res.data.critiques || []);
    } catch (err) {
      toast.error('Failed to load critiques');
    }
    setLoading(false);
  };

  useEffect(() => { fetchCritiques(); }, []);

  const filtered = critiques.filter(c => {
    // Filter by actual status (has response = completed, no response = pending)
    if (statusFilter === 'completed' && !c.response) return false;
    if (statusFilter === 'pending' && c.response) return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (c.user_name || '').toLowerCase().includes(s) ||
           (c.coach_name || '').toLowerCase().includes(s) ||
           (c.discipline || '').toLowerCase().includes(s);
  });

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  const statusBadge = (critique) => {
    const hasResponse = !!critique.response;
    if (hasResponse) return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Completed</Badge>;
    if (critique.status === 'pending' || !hasResponse) return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">Pending</Badge>;
    return <Badge variant="outline">{critique.status}</Badge>;
  };

  const getActualStatus = (critique) => {
    return critique.response ? 'completed' : 'pending';
  };

  const completedCount = critiques.filter(c => !!c.response).length;
  const pendingCount = critiques.filter(c => !c.response).length;

  const handleDelete = async (critiqueId) => {
    if (!window.confirm('Delete this critique? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/critiques/${critiqueId}`);
      toast.success('Critique deleted');
      setCritiques(prev => prev.filter(c => c.id !== critiqueId));
      if (expandedId === critiqueId) setExpandedId(null);
    } catch (err) {
      toast.error('Failed to delete critique');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-critiques-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="font-display font-bold text-2xl">{critiques.length}</p>
            <p className="text-xs text-muted-foreground uppercase">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="font-display font-bold text-2xl text-green-600">{completedCount}</p>
            <p className="text-xs text-muted-foreground uppercase">Completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="font-display font-bold text-2xl text-amber-600">{pendingCount}</p>
            <p className="text-xs text-muted-foreground uppercase">Pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by student, coach, or discipline..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="critique-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="critique-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No critiques found.</div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Student</TableHead>
                <TableHead>Coach</TableHead>
                <TableHead className="hidden sm:table-cell">Discipline</TableHead>
                <TableHead className="hidden sm:table-cell">Package</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Date</TableHead>
                <TableHead className="text-center">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(c => (
                <React.Fragment key={c.id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                    data-testid={`critique-row-${c.id}`}
                  >
                    <TableCell className="font-medium">{c.user_name || '—'}</TableCell>
                    <TableCell>{c.coach_name || '—'}</TableCell>
                    <TableCell className="hidden sm:table-cell">{c.discipline || '—'}</TableCell>
                    <TableCell className="hidden sm:table-cell capitalize">{c.critique_package || '—'}</TableCell>
                    <TableCell>{statusBadge(c)}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{formatDate(c.created_at)}</TableCell>
                    <TableCell className="text-center flex items-center justify-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                        className="text-muted-foreground hover:text-red-500 p-1 rounded"
                        title="Delete critique"
                        data-testid={`delete-critique-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedId === c.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </TableCell>
                  </TableRow>
                  {expandedId === c.id && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/30 p-4">
                        <div className="space-y-4 max-w-3xl">
                          {/* Student's request */}
                          <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">Student's Request</p>
                            <div className="bg-background p-3 rounded-lg border space-y-2">
                              <div className="flex flex-wrap gap-2 text-sm">
                                <span className="font-medium">{c.user_name}</span>
                                {c.user_region && <span className="text-muted-foreground">({c.user_region})</span>}
                                <span className="text-muted-foreground">·</span>
                                <span>{c.discipline}</span>
                                <span className="text-muted-foreground">·</span>
                                <Badge variant="outline" className="capitalize">{c.critique_package}</Badge>
                                <span className="text-muted-foreground">·</span>
                                <span className="text-muted-foreground">{c.credits_used} credits</span>
                              </div>
                              {c.note && <p className="text-sm text-muted-foreground italic">"{c.note}"</p>}
                              <div className="flex flex-wrap gap-2">
                                {c.video_link && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setVideoPlayer({ url: c.video_link, label: `${c.user_name} - Video 1` }); }}
                                    className="text-primary text-xs hover:underline inline-flex items-center gap-1"
                                  >
                                    <Play className="w-3 h-3" /> Video 1
                                  </button>
                                )}
                                {c.additional_videos?.map((v, i) => (
                                  <button
                                    key={i}
                                    onClick={(e) => { e.stopPropagation(); setVideoPlayer({ url: v, label: `${c.user_name} - Video ${i+2}` }); }}
                                    className="text-primary text-xs hover:underline inline-flex items-center gap-1"
                                  >
                                    <Play className="w-3 h-3" /> Video {i+2}
                                  </button>
                                ))}
                                {c.shared_directly && <span className="text-xs text-muted-foreground italic">Video shared directly</span>}
                              </div>
                              <p className="text-xs text-muted-foreground">Submitted: {formatDate(c.created_at)}</p>
                            </div>
                          </div>

                          {/* Coach's response */}
                          <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">Coach's Response</p>
                            {c.response ? (
                              <div className="bg-background p-3 rounded-lg border border-green-500/20 space-y-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span className="font-medium">{c.response.coach_name}</span>
                                  <span className="text-muted-foreground">·</span>
                                  <Badge variant="outline" className="capitalize">{c.response.type}</Badge>
                                  {c.response.updated_at && (
                                    <span className="text-xs text-muted-foreground">(edited)</span>
                                  )}
                                </div>
                                {c.response.type === 'text' ? (
                                  <div className="text-sm bg-muted/50 p-3 rounded whitespace-pre-wrap max-h-60 overflow-y-auto">
                                    {c.response.content}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setVideoPlayer({ url: c.response.content, label: `Coach Response - ${c.response.coach_name}` }); }}
                                      className="text-primary text-sm hover:underline inline-flex items-center gap-1"
                                    >
                                      <Play className="w-4 h-4" /> Watch Coach Response
                                    </button>
                                    <a href={c.response.content} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  </div>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  Responded: {formatDate(c.response.created_at)}
                                  {c.response.updated_at && ` · Updated: ${formatDate(c.response.updated_at)}`}
                                </p>
                              </div>
                            ) : (
                              <div className="bg-background p-3 rounded-lg border border-amber-500/20 flex items-center gap-2 text-sm text-amber-600">
                                <AlertCircle className="w-4 h-4" />
                                Awaiting coach response
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Video Player */}
      {videoPlayer && (
        <CritiqueVideoPlayer
          url={videoPlayer.url}
          label={videoPlayer.label}
          onClose={() => setVideoPlayer(null)}
        />
      )}
    </div>
  );
};

export default AdminCritiquesTab;
